import { render } from 'preact';
import { useEffect } from 'preact/hooks';
import { LocationProvider, Router, Route, lazy, ErrorBoundary, useLocation, useRoute } from 'preact-iso';
import '@fontsource-variable/inter/index.css';
import '@fontsource/caveat/latin-600.css';
import './styles/tokens.css';
import './styles/base.css';
import './styles/layout.css';
import './styles/screens.css';
import { SessionProvider } from './session';
import { OfflineBar, Toaster } from './components/ui';
import { ChromeFooter, SiteHeader } from './components/SiteLayout';
import { Home } from './pages/site/Home';
import { Features } from './pages/site/Features';
import { Pricing } from './pages/site/Pricing';
import { Faq } from './pages/site/Faq';
import { Apps } from './pages/site/Apps';
import { Ios } from './pages/site/Ios';
import { Credits } from './pages/site/Credits';
import { Privacy, Terms } from './pages/site/Legal';
import { Login, Signup } from './pages/site/Auth';
import { Forgot, Reset, Verify } from './pages/site/Recovery';
import { Copyright } from './pages/site/Copyright';
import { NotFound } from './pages/site/NotFound';

const Downloader = lazy(() => import('./pages/app/Downloader').then((m) => m.Downloader));
const Discover = lazy(() => import('./pages/app/Discover').then((m) => m.Discover));
const VideoDetail = lazy(() => import('./pages/app/VideoDetail').then((m) => m.VideoDetail));
const Library = lazy(() => import('./pages/app/Library').then((m) => m.Library));
const AiStudio = lazy(() => import('./pages/app/AiStudio').then((m) => m.AiStudio));
const TranscriptView = lazy(() => import('./pages/app/TranscriptView').then((m) => m.TranscriptView));
const Account = lazy(() => import('./pages/app/Account').then((m) => m.Account));
const Admin = lazy(() => import('./pages/app/Admin').then((m) => m.Admin));

/** Old (v1 web app) routes → new screens, keeping query strings. */
function Redirect({ to }: { to: (q: Record<string, string>, params: Record<string, string>) => string }) {
  const { query, route } = useLocation();
  const { params } = useRoute();
  useEffect(() => { route(to(query, params), true); }, []);
  return null;
}
const qs = (q: Record<string, string>, drop: string[] = []) => {
  const p = new URLSearchParams(Object.entries(q).filter(([k]) => !drop.includes(k)));
  const s = p.toString();
  return s ? `?${s}` : '';
};

const OldApp = () => <Redirect to={(q) => `/download${q.tab === 'hosted' ? '?tab=cloud' : qs(q, ['tab'])}`} />;
const OldDiscover = () => <Redirect to={(q) => `/discover${qs(q)}`} />;
const OldVideo = () => <Redirect to={(_q, p) => `/discover/v/${encodeURIComponent(p.id ?? '')}`} />;
const OldLibrary = () => <Redirect to={(q) => `/library${qs(q)}`} />;

// Lazily loaded pages can finish rendering after the user has already moved on (e.g. opened a menu).
// Remember what had focus when navigation started; a late route change only moves focus if the user
// hasn't focused anything else since.
let focusAtNav: Element | null = null;
document.addEventListener('click', (e) => {
  if ((e.target as Element | null)?.closest?.('a[href^="/"]')) focusAtNav = document.activeElement;
}, true);
const userMovedFocus = () => {
  const a = document.activeElement;
  return !!a && a !== document.body && a !== focusAtNav;
};

function onRouteChange() {
  // Move focus to the new page's main landmark for screen-reader and keyboard users.
  requestAnimationFrame(() => {
    window.scrollTo(0, 0);
    if (userMovedFocus()) { focusAtNav = null; return; }
    focusAtNav = null;
    const main = document.getElementById('main');
    if (main && document.activeElement && document.activeElement !== document.body && !main.contains(document.activeElement)) main.focus({ preventScroll: true });
  });
}

function App() {
  return (
    <LocationProvider>
      <SessionProvider>
        <OfflineBar />
        <SiteHeader />
        <ErrorBoundary>
          <Router onRouteChange={onRouteChange}>
            <Route path="/" component={Home} />
            <Route path="/discover" component={Discover} />
            <Route path="/discover/v/:id" component={VideoDetail} />
            <Route path="/download" component={Downloader} />
            <Route path="/library" component={Library} />
            <Route path="/features" component={Features} />
            <Route path="/pricing" component={Pricing} />
            <Route path="/faq" component={Faq} />
            <Route path="/apps" component={Apps} />
            <Route path="/ios" component={Ios} />
            <Route path="/credits" component={Credits} />
            <Route path="/privacy" component={Privacy} />
            <Route path="/terms" component={Terms} />
            <Route path="/login" component={Login} />
            <Route path="/signup" component={Signup} />
            <Route path="/forgot" component={Forgot} />
            <Route path="/reset" component={Reset} />
            <Route path="/verify" component={Verify} />
            <Route path="/copyright" component={Copyright} />
            <Route path="/app/ai" component={AiStudio} />
            <Route path="/app/ai/t/:id" component={TranscriptView} />
            <Route path="/app/account" component={Account} />
            <Route path="/app/admin" component={Admin} />
            <Route path="/app" component={OldApp} />
            <Route path="/app/discover" component={OldDiscover} />
            <Route path="/app/discover/v/:id" component={OldVideo} />
            <Route path="/app/library" component={OldLibrary} />
            <Route default component={NotFound} />
          </Router>
        </ErrorBoundary>
        <ChromeFooter />
        <Toaster />
      </SessionProvider>
    </LocationProvider>
  );
}

render(<App />, document.getElementById('app')!);
